import { ethers } from 'ethers';

export const ZERO_BYTES32 = '0x0000000000000000000000000000000000000000000000000000000000000000';

export interface AgreementPayloadInput {
  schemaVersion: number;
  chainId: number;
  agreementSchemaUID: string;
  fileverseCID: string;
  identityUID: string;
  propertyUID: string;
  questionnaireUID: string;
  warrantyUIDs: string[];
  walletAddress: string;
  timestamp?: number;
  nonce: string;
}

export interface CanonicalAgreementPayload {
  schemaVersion: number;
  chainId: number;
  agreementSchemaUID: string;
  fileverseCID: string;
  identityUID: string;
  propertyUID: string;
  questionnaireUID: string;
  warrantyUIDs: string[];
  walletAddress: string;
  timestamp: number;
  nonce: string;
}

export interface BuiltAgreementPayload {
  payload: CanonicalAgreementPayload;
  encodedData: string;
  payloadHash: string;
}

export const deriveWarrantyUIDs = (ids: string[]): string[] => {
  return ids.map((id) => ethers.id(id.trim().toLowerCase()));
};

export const normalizeAgreementUID = (uid?: string): string => {
  if (!uid) {
    return ZERO_BYTES32;
  }

  const normalized = uid.trim().toLowerCase();
  return /^0x[a-f0-9]{64}$/.test(normalized) ? normalized : ZERO_BYTES32;
};

export const normalizeAgreementNonce = (nonce?: string): string => {
  if (!nonce) {
    return ZERO_BYTES32;
  }

  const normalized = nonce.trim().toLowerCase();
  return /^0x[a-f0-9]{64}$/.test(normalized) ? normalized : ZERO_BYTES32;
};

export const buildCanonicalAgreementHash = (payload: CanonicalAgreementPayload): string => {
  const coder = ethers.AbiCoder.defaultAbiCoder();
  const fileverseCIDHash = ethers.keccak256(ethers.toUtf8Bytes(payload.fileverseCID));
  const warrantyUIDsHash = ethers.keccak256(coder.encode(['bytes32[]'], [payload.warrantyUIDs]));

  return ethers.keccak256(
    coder.encode(
      [
        'uint16',
        'uint256',
        'bytes32',
        'bytes32',
        'bytes32',
        'bytes32',
        'bytes32',
        'bytes32',
        'address',
        'uint256',
        'bytes32',
      ],
      [
        payload.schemaVersion,
        payload.chainId,
        payload.agreementSchemaUID,
        fileverseCIDHash,
        payload.identityUID,
        payload.propertyUID,
        payload.questionnaireUID,
        warrantyUIDsHash,
        payload.walletAddress,
        payload.timestamp,
        payload.nonce,
      ]
    )
  );
};

export const buildCanonicalAgreementPayload = (
  input: AgreementPayloadInput
): BuiltAgreementPayload => {
  const schemaVersion = input.schemaVersion;
  const chainId = input.chainId;
  const agreementSchemaUID = normalizeAgreementUID(input.agreementSchemaUID);
  const fileverseCID = (input.fileverseCID || '').trim();
  const walletAddress = (input.walletAddress || '').trim();
  const timestamp = input.timestamp ?? Math.floor(Date.now() / 1000);
  const nonce = normalizeAgreementNonce(input.nonce);

  const identityUID = normalizeAgreementUID(input.identityUID);
  const propertyUID = normalizeAgreementUID(input.propertyUID);
  const questionnaireUID = normalizeAgreementUID(input.questionnaireUID);
  const warrantyUIDs = (input.warrantyUIDs || [])
    .map((uid) => normalizeAgreementUID(uid))
    .filter(Boolean);

  const missingFields: string[] = [];
  if (!Number.isInteger(schemaVersion) || schemaVersion <= 0) missingFields.push('schemaVersion');
  if (!Number.isInteger(chainId) || chainId <= 0) missingFields.push('chainId');
  if (!input.agreementSchemaUID?.trim()) missingFields.push('agreementSchemaUID');
  if (!fileverseCID) missingFields.push('fileverseCID');
  if (!input.identityUID?.trim()) missingFields.push('identityUID');
  if (!input.propertyUID?.trim()) missingFields.push('propertyUID');
  if (!input.questionnaireUID?.trim()) missingFields.push('questionnaireUID');
  if (!(input.warrantyUIDs || []).map((uid) => uid.trim()).filter(Boolean).length) missingFields.push('warrantyUIDs');
  if (!walletAddress) missingFields.push('walletAddress');
  if (!Number.isFinite(timestamp) || timestamp <= 0) missingFields.push('timestamp');
  if (!input.nonce?.trim()) missingFields.push('nonce');

  if (missingFields.length) {
    throw new Error(`Agreement payload is incomplete. Missing required fields: ${missingFields.join(', ')}`);
  }

  if (!ethers.isAddress(walletAddress)) {
    throw new Error(`Agreement payload walletAddress is invalid: ${walletAddress}`);
  }

  if (
    agreementSchemaUID === ZERO_BYTES32 ||
    identityUID === ZERO_BYTES32 ||
    propertyUID === ZERO_BYTES32 ||
    questionnaireUID === ZERO_BYTES32
  ) {
    throw new Error('Agreement payload includes invalid UID format for agreementSchemaUID, identityUID, propertyUID, or questionnaireUID.');
  }

  if (warrantyUIDs.some((uid) => uid === ZERO_BYTES32)) {
    throw new Error('Agreement payload includes invalid UID format in warrantyUIDs.');
  }

  if (nonce === ZERO_BYTES32) {
    throw new Error('Agreement payload includes invalid nonce format. Expected bytes32 hex string.');
  }

  const payload: CanonicalAgreementPayload = {
    schemaVersion,
    chainId,
    agreementSchemaUID,
    fileverseCID,
    identityUID,
    propertyUID,
    questionnaireUID,
    warrantyUIDs,
    walletAddress,
    timestamp,
    nonce,
  };

  const encodedData = ethers.AbiCoder.defaultAbiCoder().encode(
    ['string', 'bytes32', 'bytes32', 'bytes32', 'bytes32[]', 'address', 'uint256'],
    [
      payload.fileverseCID,
      payload.identityUID,
      payload.propertyUID,
      payload.questionnaireUID,
      payload.warrantyUIDs,
      payload.walletAddress,
      payload.timestamp,
    ]
  );

  const payloadHash = buildCanonicalAgreementHash(payload);

  return {
    payload,
    encodedData,
    payloadHash,
  };
};